import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Brain, 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle,
  Play,
  Clock,
  TrendingUp,
  BarChart3,
  FileText,
  Download,
  Trash2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatWithUTC } from '@/lib/datetime';
import jsPDF from 'jspdf';

interface AnalysisResult {
  id: string;
  type: string;
  status: 'completed' | 'pending' | 'failed';
  created_at: string;
  findings_count: number;
  confidence: number;
  summary: any;
}

interface Finding {
  id: string;
  finding_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  created_at: string;
  data_reference?: any;
}

interface ModuleAnalysesTabProps {
  auditId: string;
  moduleId: string;
  onStartAnalysis?: () => void;
}

const getSeverityIcon = (severity: 'low' | 'medium' | 'high' | 'critical') => {
  switch (severity) {
    case 'critical':
    case 'high':
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case 'medium':
      return <AlertTriangle className="h-4 w-4 text-warning" />;
    case 'low':
      return <CheckCircle className="h-4 w-4 text-success" />;
    default:
      return <CheckCircle className="h-4 w-4" />;
  }
};

const getSeverityColor = (severity: 'low' | 'medium' | 'high' | 'critical') => {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'medium':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'low':
      return 'bg-success/10 text-success border-success/20';
    default:
      return 'bg-muted/50';
  }
};

const getAnalysisTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'tax_account': 'Skattekontoanalys',
    'vat_analysis': 'Momsanalys',
    'payroll_analysis': 'Löneanalys',
    'revenue_analysis': 'Intäktsanalys',
    'cost_analysis': 'Kostnadsanalys'
  };
  return labels[type] || type;
};

// Removed - using formatWithUTC from @/lib/datetime

export function ModuleAnalysesTab({ auditId, moduleId, onStartAnalysis }: ModuleAnalysesTabProps) {
  const { toast } = useToast();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [premiumReport, setPremiumReport] = useState<string | null>(null);
  const [revisionHistory, setRevisionHistory] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revisionToDelete, setRevisionToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState<{
    queuedBatches: number;
    processingBatches: number;
    completedBatches: number;
    failedBatches: number;
    totalTokens: number;
    totalCost: number;
  } | null>(null);
  const [completionTimestamp, setCompletionTimestamp] = useState<number | null>(null);

  useEffect(() => {
    console.log('[ModuleAnalysesTab] Component mounted/updated:', { auditId, moduleId });
    fetchAnalysisData();
  }, [auditId, moduleId]);

  // Add manual refresh function
  const refreshAnalysisData = () => {
    console.log('[ModuleAnalysesTab] Manual refresh triggered');
    fetchAnalysisData();
  };

  // Poll stats for real-time updates
  const pollStats = async () => {
    try {
      // 1️⃣ Först: hämta pågående revisions (processing)
      const { data: processingRevisions, error: processingError } = await supabase
        .from('revisions')
        .select('id, total_tokens_in, total_tokens_out, total_cost_sek, status, created_at')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .eq('status', 'processing')
        .order('created_at', { ascending: false });

      if (processingError) throw processingError;

      let revisionsToUse = processingRevisions || [];
      let isProcessing = true;

      // 2️⃣ Om ingen pågående revision finns, hämta den senaste (oavsett status)
      if (revisionsToUse.length === 0) {
        const { data: latestRevision, error: latestError } = await supabase
          .from('revisions')
          .select('id, total_tokens_in, total_tokens_out, total_cost_sek, status, created_at')
          .eq('audit_id', auditId)
          .eq('module_type', moduleId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (latestError) throw latestError;
        
        if (latestRevision && latestRevision.length > 0) {
          revisionsToUse = latestRevision;
          isProcessing = latestRevision[0].status === 'processing';
          
          // ⭐ Om revision precis blev completed, spara timestamp och uppdatera findings
          if (!isProcessing && !completionTimestamp) {
            setCompletionTimestamp(Date.now());
            // Automatiskt hämta findings när analys är klar
            fetchAnalysisData();
            fetchRevisionHistory();
          }
        }
      } else {
        // Reset completion timestamp om vi har en pågående revision
        if (completionTimestamp) {
          setCompletionTimestamp(null);
        }
      }

      // 3️⃣ Om inga revisions finns, dölj stats
      if (revisionsToUse.length === 0) {
        setStats(null);
        return;
      }

      // 4️⃣ Om revision är completed OCH 10 sekunder har gått, dölj stats
      if (!isProcessing && completionTimestamp) {
        const secondsSinceCompletion = (Date.now() - completionTimestamp) / 1000;
        if (secondsSinceCompletion > 10) {
          setStats(null);
          return; // ⭐ Sluta visa stats, polling fortsätter för framtida analyser
        }
      }

      // 5️⃣ Hämta batch-status
      const revisionIds = revisionsToUse.map(r => r.id);
      const { data: batches, error: batchesError } = await supabase
        .from('batches')
        .select('state')
        .in('revision_id', revisionIds);

      if (batchesError) throw batchesError;

      const queuedBatches = batches?.filter(b => b.state === 'queued').length || 0;
      const processingBatches = batches?.filter(b => b.state === 'processing').length || 0;
      const completedBatches = batches?.filter(b => b.state === 'completed').length || 0;
      const failedBatches = batches?.filter(b => b.state === 'failed').length || 0;

      const totalTokens = revisionsToUse.reduce((sum, r: any) => 
        sum + (r.total_tokens_in || 0) + (r.total_tokens_out || 0), 0);
      const totalCost = revisionsToUse.reduce((sum, r: any) => 
        sum + (r.total_cost_sek || 0), 0);

      // 6️⃣ Visa stats (ingen setTimeout här!)
      setStats({
        queuedBatches,
        processingBatches,
        completedBatches,
        failedBatches,
        totalTokens,
        totalCost,
      });
    } catch (err) {
      console.error('[pollStats] Error:', err);
    }
  };

  // Fetch revision history for delete functionality
  const fetchRevisionHistory = async () => {
    try {
      const { data: revisions, error } = await supabase
        .from('revisions')
        .select('id, created_at, file_name, status')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .order('created_at', { ascending: false })

      if (error) throw error

      // For each revision, fetch findings count and row count
      const revisionsWithStats = await Promise.all(
        (revisions || []).map(async (rev) => {
          const { data: aggregate } = await supabase
            .from('aggregates')
            .select('summary_json')
            .eq('revision_id', rev.id)
            .single()

          const findings = (aggregate?.summary_json as any)?.findings || []
          const totalRows = (aggregate?.summary_json as any)?.totalRows || 0
          
          return {
            ...rev,
            totalRows,
            findingsCount: findings.length,
            criticalCount: findings.filter((f: any) => f.severity === 'critical').length,
            highCount: findings.filter((f: any) => f.severity === 'high').length
          }
        })
      )

      setRevisionHistory(revisionsWithStats)
    } catch (err) {
      console.error('Error fetching revision history:', err)
    }
  }

  const handleDeleteRevision = async () => {
    if (!revisionToDelete) return

    try {
      setIsDeleting(true)

      // Delete batches first (foreign key constraints)
      const { error: batchesError } = await supabase
        .from('batches')
        .delete()
        .eq('revision_id', revisionToDelete)

      if (batchesError) throw batchesError

      // Delete aggregates
      const { error: aggregatesError } = await supabase
        .from('aggregates')
        .delete()
        .eq('revision_id', revisionToDelete)

      if (aggregatesError) throw aggregatesError

      // Delete revision itself
      const { error: revisionError } = await supabase
        .from('revisions')
        .delete()
        .eq('id', revisionToDelete)

      if (revisionError) throw revisionError

      toast({
        title: "Revision raderad",
        description: "Revisionen och alla relaterade data har raderats",
      })

      // Update UI
      await fetchAnalysisData()
      await fetchRevisionHistory()
      setDeleteDialogOpen(false)
      setRevisionToDelete(null)
    } catch (err) {
      console.error('Error deleting revision:', err)
      toast({
        title: "Fel vid radering",
        description: err instanceof Error ? err.message : "Kunde inte radera revisionen",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Set up polling interval
  useEffect(() => {
    let interval: number | undefined;
    
    pollStats();
    interval = window.setInterval(pollStats, 2000);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [auditId, moduleId]);

  useEffect(() => {
    fetchAnalysisData();
    fetchRevisionHistory();
  }, [auditId, moduleId]);

  // Realtime subscription för automatisk uppdatering av findings
  useEffect(() => {
    const channel = supabase
      .channel(`module-analysis-${auditId}-${moduleId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'revisions',
          filter: `audit_id=eq.${auditId}`
        },
        (payload) => {
          console.log('[ModuleAnalysesTab] Realtime revision update:', payload);
          if (payload.new.status === 'completed' && payload.new.module_type === moduleId) {
            console.log('[ModuleAnalysesTab] Analysis completed, fetching new data...');
            fetchAnalysisData();
            fetchRevisionHistory();
            toast({
              title: '✅ Analys klar',
              description: 'Resultaten har uppdaterats automatiskt',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [auditId, moduleId]);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      
      // Fetch completed revisions for this audit and module
      const { data: revisions, error: revisionsError } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(5);

      if (revisionsError) throw revisionsError;

      if (!revisions || revisions.length === 0) {
        setFindings([]);
        setLoading(false);
        return;
      }

      // Fetch aggregates for these revisions (SORTED by created_at DESC)
      const revisionIds = revisions.map(r => r.id);
      const { data: aggregates, error: aggregatesError } = await supabase
        .from('aggregates')
        .select('*')
        .in('revision_id', revisionIds)
        .order('created_at', { ascending: false }); // ✅ FIX: Hämta senaste aggregaten först

      if (aggregatesError) throw aggregatesError;

      // Extract findings from the most recent aggregate
      if (aggregates && aggregates.length > 0) {
        const latestAggregate = aggregates[0]; // Nu är detta garanterat den senaste
        const summaryJson = latestAggregate.summary_json as any;
        
        console.log('[ModuleAnalysesTab] Extracting findings from aggregate:', {
          aggregateId: latestAggregate.id,
          findingsCount: summaryJson?.findings?.length || 0,
          sampleFinding: summaryJson?.findings?.[0]
        });
        
        const extractedFindings = (summaryJson?.findings || []).map((f: any, index: number) => ({
          id: f.id || `finding_${index}`,
          finding_type: f.findingType || f.category || f.type || 'unknown',
          severity: f.severity || 'low',
          description: f.message || f.description || f.shortMessage || '',
          created_at: latestAggregate.created_at,
          data_reference: {
            details: f.details || f.longMessage || '',
            period: f.period || f.affectedPeriod || '',
            amount: f.amount || 0,
            recommendation: f.recommendation || '',
            score: f.score,
            dataReferences: f.dataReferences || null,
            ...f
          }
        }));
        
        console.log('[ModuleAnalysesTab] Extracted findings:', {
          count: extractedFindings.length,
          severities: extractedFindings.map(f => f.severity),
          types: extractedFindings.map(f => f.finding_type)
        });
        
        setFindings(extractedFindings);
      } else {
        console.log('[ModuleAnalysesTab] No aggregates found');
        setFindings([]);
      }
      
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      setFindings([]);
    } finally {
      setLoading(false);
    }
  };

  const startTaxAccountAnalysis = () => {
    if (onStartAnalysis) {
      onStartAnalysis();
    } else {
      toast({
        title: "Analys startad",
        description: "Skattekontoanalysen påbörjas i Skattekonto-fliken"
      });
    }
  };

  const generateReport = async () => {
    if (findings.length === 0) {
      toast({
        title: "Inga fynd",
        description: "Det finns inga fynd att generera rapport från",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingReport(true);
      
      console.log('[ModuleAnalysesTab] Generating report with findings:', {
        count: findings.length,
        sample: findings[0],
        allFindings: findings
      });

      const { data, error } = await supabase.functions.invoke('generate-module-report', {
        body: {
          findings: findings.map(f => ({
            severity: f.severity,
            category: f.finding_type,
            message: f.description,
            details: f.data_reference?.details || '',
            affectedPeriod: f.data_reference?.period || '',
            amount: f.data_reference?.amount || 0,
            score: f.data_reference?.score,
            rowNumbers: f.data_reference?.dataReferences?.rowNumbers || [],
            dates: f.data_reference?.dataReferences?.dates || []
          })),
          summary: {
            total_findings: findings.length,
            high_risk: findings.filter(f => f.severity === 'high' || f.severity === 'critical').length,
            medium_risk: findings.filter(f => f.severity === 'medium').length,
            low_risk: findings.filter(f => f.severity === 'low').length
          },
          moduleType: moduleId,
          companyName: null
        }
      });
      
      console.log('[ModuleAnalysesTab] Report generation response:', { data, error });

      if (error) throw error;

      setPremiumReport(data.report);
      
      toast({
        title: "Rapport genererad",
        description: "Rapporten är nu tillgänglig för visning och export",
      });

    } catch (err) {
      console.error('Report generation error:', err);
      toast({
        title: "Fel vid rapportgenerering",
        description: err instanceof Error ? err.message : "Kunde inte generera rapport",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const getModuleReportTitle = (moduleId: string): string => {
    const titles: Record<string, string> = {
      'taxes_fees': 'Modulrapport - Skattekontoanalys',
      'vat_analysis': 'Modulrapport - Momsanalys',
      'payroll_analysis': 'Modulrapport - Löneanalys',
      'revenue_analysis': 'Modulrapport - Intäktsanalys',
      'cost_analysis': 'Modulrapport - Kostnadsanalys'
    };
    return titles[moduleId] || 'Modulrapport';
  };

  const exportPDF = () => {
    if (!premiumReport) {
      toast({
        title: "Ingen rapport",
        description: "Generera en rapport först",
        variant: "destructive",
      });
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    const reportTitle = getModuleReportTitle(moduleId);
    doc.text(reportTitle.toUpperCase(), margin, yPos);
    yPos += 12;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Genererad: ${new Date().toLocaleDateString('sv-SE')}`, margin, yPos);
    yPos += 10;

    // Statistics
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('STATISTIK', margin, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Totalt antal fynd: ${analysisStats.total}`, margin, yPos);
    yPos += 6;
    
    doc.setTextColor(220, 38, 38);
    doc.text(`Kritiska/Höga: ${analysisStats.high}`, margin, yPos);
    yPos += 6;
    
    doc.setTextColor(245, 158, 11);
    doc.text(`Medel: ${analysisStats.medium}`, margin, yPos);
    yPos += 6;
    
    doc.setTextColor(34, 197, 94);
    doc.text(`Låga: ${analysisStats.low}`, margin, yPos);
    yPos += 12;

    // Report content
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT', margin, yPos);
    yPos += 8;

    const reportText = premiumReport
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const reportLines = doc.splitTextToSize(reportText, pageWidth - 2 * margin);
    reportLines.forEach((line: string) => {
      if (yPos > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
      }
      doc.text(line, margin, yPos);
      yPos += 5;
    });

    // Findings - Add page break
    if (findings.length > 0) {
      doc.addPage();
      yPos = margin;
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('DETALJERADE FYND', margin, yPos);
      yPos += 8;

      findings.forEach((finding, index) => {
        if (yPos > pageHeight - 35) {
          doc.addPage();
          yPos = margin;
        }

        const severityColors: { [key: string]: [number, number, number] } = {
          critical: [220, 38, 38],
          high: [234, 88, 12],
          medium: [245, 158, 11],
          low: [34, 197, 94]
        };
        const color = severityColors[finding.severity] || [100, 100, 100];

        doc.setDrawColor(...color);
        doc.setLineWidth(1);
        doc.line(margin, yPos, margin, yPos + 25);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...color);
        const severityText = finding.severity === 'critical' ? 'KRITISK' :
                            finding.severity === 'high' ? 'HÖG' :
                            finding.severity === 'medium' ? 'MEDEL' : 'LÅG';
        doc.text(severityText, margin + 3, yPos + 5);

        doc.setTextColor(0);
        doc.text(`${index + 1}. ${finding.finding_type}`, margin + 20, yPos + 5);
        yPos += 8;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50);
        const descLines = doc.splitTextToSize(finding.description, pageWidth - 2 * margin - 3);
        descLines.forEach((line: string) => {
          if (yPos > pageHeight - margin) {
            doc.addPage();
            yPos = margin;
          }
          doc.text(line, margin + 3, yPos);
          yPos += 4;
        });

        yPos += 6;
      });
    }

    const filename = `analys-${moduleId}-${new Date().getTime()}.pdf`;
    doc.save(filename);

    toast({
      title: "Export lyckades",
      description: `PDF-rapport har laddats ner: ${filename}`,
    });
  };

  const convertMarkdownToHtml = (text: string): string => {
    return text
      // Fetstil: **text** eller __text__
      .replace(/\*\*\s*(.*?)\s*\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Kursiv: *text* eller _text_
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Bullet-listor: - text
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Radbrytningar
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  const analysisStats = {
    total: findings.length,
    high: findings.filter(f => f.severity === 'high' || f.severity === 'critical').length,
    medium: findings.filter(f => f.severity === 'medium').length,
    low: findings.filter(f => f.severity === 'low').length,
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Analyser
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshAnalysisData}
            disabled={loading}
            className="gap-2"
          >
            <Clock className="h-4 w-4" />
            {loading ? "Laddar..." : "Uppdatera"}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          
          {/* Real-time Statistics */}
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 animate-pulse text-blue-600" />
                  Realtidsstatistik
                </CardTitle>
                <CardDescription>Live-uppdatering av analysprocessen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">I kö</div>
                    <div className="text-2xl font-bold">{stats.queuedBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Processar</div>
                    <div className="text-2xl font-bold">{stats.processingBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Klara</div>
                    <div className="text-2xl font-bold text-green-600">{stats.completedBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Fel</div>
                    <div className="text-2xl font-bold text-red-600">{stats.failedBatches}</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Totalt batchar:</span>
                    <span className="font-medium">
                      {stats.completedBatches} / {stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches}
                    </span>
                  </div>
                  <Progress 
                    value={
                      ((stats.completedBatches / 
                        (stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches || 1)) * 100)
                    } 
                  />
                </div>

                {stats.totalTokens > 0 && (
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Totala tokens:</span>
                      <span className="font-medium">{stats.totalTokens.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Revision History */}
          {revisionHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Analyshistorik ({revisionHistory.length})
                </CardTitle>
                <CardDescription>
                  Alla körda analyser för denna modul. Den senaste visas i översikten.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revisionHistory.map((revision, index) => (
                    <Card key={revision.id} className={index === 0 ? 'border-primary/50 bg-primary/5' : ''}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {index === 0 && (
                                <Badge variant="default" className="text-xs">
                                  Senaste (visas i översikt)
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {revision.status === 'completed' ? '✓ Klar' : 
                                 revision.status === 'processing' ? '⏳ Processar' : 
                                 revision.status === 'failed' ? '✗ Misslyckad' : revision.status}
                              </Badge>
                            </div>
                            
                            <div className="text-sm space-y-1">
                              <div className="font-medium">
                                {revision.file_name || 'Ingen fil'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatWithUTC(revision.created_at)}
                              </div>
                              <div className="flex gap-3 text-xs text-muted-foreground">
                                <span>{revision.totalRows || 0} rader</span>
                                <span>•</span>
                                <span>{revision.findingsCount} fynd</span>
                                {revision.criticalCount > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-destructive font-medium">
                                      {revision.criticalCount} kritiska
                                    </span>
                                  </>
                                )}
                                {revision.highCount > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-warning font-medium">
                                      {revision.highCount} höga
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setRevisionToDelete(revision.id)
                              setDeleteDialogOpen(true)
                            }}
                            disabled={revisionHistory.length === 1}
                            title={revisionHistory.length === 1 ? 'Kan inte radera den enda revisionen' : 'Radera revision'}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Actions */}
          {findings.length > 0 && (
            <div className="flex gap-3">
              <Button 
                onClick={generateReport} 
                disabled={isGeneratingReport}
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                {isGeneratingReport ? "Genererar..." : "Generera rapport"}
              </Button>
              {premiumReport && (
                <Button 
                  onClick={exportPDF}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Exportera PDF
                </Button>
              )}
            </div>
          )}

          {/* Report Preview */}
          {premiumReport && (
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Genererad rapport
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(premiumReport) }}
                />
              </CardContent>
            </Card>
          )}

          {/* Analysis Summary */}
          {findings.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Totalt</p>
                    <p className="text-2xl font-bold">{analysisStats.total}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Hög risk</p>
                    <p className="text-2xl font-bold text-destructive">{analysisStats.high}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning/10 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Medel risk</p>
                    <p className="text-2xl font-bold text-warning">{analysisStats.medium}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-success/10 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Låg risk</p>
                    <p className="text-2xl font-bold text-success">{analysisStats.low}</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Module-specific Analysis Actions */}
          {moduleId === 'taxes_fees' && (
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="p-3 bg-primary/10 rounded-full w-fit mx-auto">
                    <Brain className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Skattekontoanalys</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Analysera uppladdade skattekontofiler för sena betalningar och avvikelser
                    </p>
                    {findings.length > 0 && (
                      <div className="p-3 mb-4 bg-warning/10 rounded-lg border border-warning/20">
                        <div className="flex items-center gap-2 text-sm text-warning-foreground">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <strong>Observera:</strong> En ny analys kommer att ersätta alla befintliga analysresultat för denna modul.
                        </div>
                      </div>
                    )}
                    <Button onClick={startTaxAccountAnalysis} className="gap-2">
                      <Play className="h-4 w-4" />
                      Starta ny skattekontoanalys
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Findings List */}
          {findings.length > 0 ? (
            <div className="space-y-4">
              <h3 className="font-semibold">Identifierade avvikelser ({findings.length})</h3>
              {findings.map((finding) => (
                <Card key={finding.id} className="border-l-4 border-l-primary/20">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getSeverityIcon(finding.severity)}
                      </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className={getSeverityColor(finding.severity)}>
                              {finding.severity === 'critical' ? 'Kritisk' :
                               finding.severity === 'high' ? 'Hög' : 
                               finding.severity === 'medium' ? 'Medel' : 'Låg'} risk
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {finding.finding_type}
                            </Badge>
                            {finding.data_reference?.source_file && (
                              <Badge variant="outline" className="text-xs bg-muted">
                                📄 {finding.data_reference.source_file}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium mb-1">{finding.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatWithUTC(finding.created_at)}
                          </p>
                          
                          {/* Data References */}
                          {finding.data_reference?.dataReferences && (
                            <div className="mt-4 pt-4 border-t space-y-2">
                              <div className="text-sm font-medium">📌 Referenser i rådata:</div>
                              
                              {finding.data_reference.dataReferences.rowNumbers && (
                                <div className="text-xs text-muted-foreground">
                                  <strong>Rader:</strong> {finding.data_reference.dataReferences.rowNumbers.join(', ')}
                                </div>
                              )}
                              
                              {finding.data_reference.dataReferences.dates && (
                                <div className="text-xs text-muted-foreground">
                                  <strong>Datum:</strong> {finding.data_reference.dataReferences.dates.join(', ')}
                                </div>
                              )}
                              
                              {finding.data_reference.dataReferences.transactionTypes && (
                                <div className="text-xs text-muted-foreground">
                                  <strong>Transaktioner:</strong> {finding.data_reference.dataReferences.transactionTypes.join(', ')}
                                </div>
                              )}
                              
                              {finding.data_reference.dataReferences.amounts && (
                                <div className="text-xs text-muted-foreground">
                                  <strong>Belopp:</strong> {finding.data_reference.dataReferences.amounts.map(a => a.toLocaleString()).join(', ')} SEK
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Inga analyser har körts än</p>
              <p className="text-sm">
                {moduleId === 'taxes_fees' 
                  ? 'Använd Skattekonto-fliken för att ladda upp dokument och starta analys'
                  : 'Ladda upp dokument för att kunna starta analyser'
                }
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Radera revision?</AlertDialogTitle>
          <AlertDialogDescription>
            Detta kommer att permanent radera denna revision och alla relaterade data:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Alla analysresultat (findings)</li>
              <li>Alla batchar och processdata</li>
              <li>Aggregerad statistik</li>
            </ul>
            <p className="mt-3 font-medium text-destructive">
              Denna åtgärd kan inte ångras.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            Avbryt
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteRevision}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Raderar..." : "Radera revision"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}